import { useCallback, useEffect } from 'react'
import { type BlockerFunction, useBlocker } from 'react-router'

/**
 * Guards against leaving the current page while `enabled` is true, covering
 * BOTH exit paths:
 *
 * 1. Browser unload (tab close, reload, external navigation): a `beforeunload`
 *    listener calls `preventDefault()` so the browser shows its native
 *    "Leave site?" prompt. Browsers ignore custom text here for security, so
 *    `message` only applies to the in-app prompt below.
 * 2. In-app navigation (React Router): `useBlocker` intercepts a route change
 *    and we `window.confirm(message)`; on confirm the blocked navigation
 *    proceeds, otherwise it is reset (the user stays).
 *
 * Requires a data router (`createBrowserRouter` / `RouterProvider`), which the
 * portal uses. The blocker only fires on a genuine location change while
 * enabled, so an in-place re-render or a same-URL navigation never prompts.
 *
 * Intended for transient "work in flight" states (e.g. an AI generation
 * actively streaming edits into the open editor), NOT as a general unsaved-
 * changes guard — keep `enabled` tightly scoped to the in-flight window.
 * @param enabled - Whether the guard is active.
 * @param message - Prompt text for the in-app confirm dialog.
 */
export function useNavigationGuard(enabled: boolean, message: string): void {
  // ---- Browser unload (tab close / reload / external nav) ----
  useEffect(() => {
    if (!enabled) {
      return
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      // Calling preventDefault (and, for older browsers, setting returnValue)
      // triggers the browser's native confirmation dialog.
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [enabled])

  // ---- In-app navigation (React Router) ----
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      enabled && currentLocation.pathname !== nextLocation.pathname,
    [enabled]
  )
  const blocker = useBlocker(shouldBlock)

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return
    }
    if (window.confirm(message)) {
      blocker.proceed()
    } else {
      blocker.reset()
    }
  }, [blocker, message])

  /* If the guard turns off (generation finished) while a navigation is still
   * sitting blocked — e.g. the stream completed in the moment between the
   * blocker firing and the user answering — release it so the app isn't wedged
   * waiting on a prompt that no longer applies. */
  useEffect(() => {
    if (!enabled && blocker.state === 'blocked') {
      blocker.proceed()
    }
  }, [enabled, blocker])
}
